import {
  createContext,
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
  type RefObject,
} from "react";
import { formatText } from "../i18n/texts";
import { BheliLoader } from "../ui/brand";
import { Toast } from "../ui/feedback";
import { ChatIcon } from "../ui/icons/Icon";
import { useRowHoverOpen } from "../ui/rowHover";
import { TaskWindow } from "../ui/TaskWindow";
import { contains, text, type TeamUi } from "./teamText";

// Piezas de Chat › Equipo con el mismo HTML que el portal actual: ChatMenu y ChatBottomSheet (menú C11 con su fondo inerte),
// TeamWindow (C30/C12), TeamToast (C13), TeamEmpty (C18), TeamSearch (C06), TeamPager (C28), TeamSwitch (C09),
// TeamFilterMenu y TeamFilterChoices (C07) y el avatar del Chat.

export const TeamContext = createContext<TeamUi | null>(null);
export function useTeam(): TeamUi {
  const ui = useContext(TeamContext);
  if (!ui) throw new Error("Sin contexto de Equipo.");
  return ui;
}
/** T(key) de OperatingComponentBase. */
export function useT(): (key: string) => string {
  const ui = useTeam();
  return useCallback((key: string) => text(ui, key), [ui]);
}

/** Cargador de Equipo («tm-more» con el loader de marca). */
export function TeamLoader({ withText = false }: { withText?: boolean }) {
  const ui = useTeam();
  const loader = <BheliLoader size={24} theme={ui.theme} label={text(ui, "BRAND_LOADING")} />;
  return withText ? (
    <>
      {loader} {text(ui, "BRAND_LOADING")}
    </>
  ) : (
    loader
  );
}
export function More() {
  return (
    <div className="tm-more">
      <TeamLoader />
    </div>
  );
}

// ---- Menú (ChatMenu + ChatStore): uno abierto a la vez por vista ----

// El estado vive fuera de React y cada menú se suscribe a si él está abierto: abrir o cerrar un ⋮ redibuja solo ese menú
// (y el que se cierra), no la lista entera.
type MenuStore = {
  setOpen: (id: string | null) => void;
  current: () => string | null;
  subscribe: (listener: () => void) => () => void;
};
function createMenuStore(): MenuStore {
  let open: string | null = null;
  const listeners = new Set<() => void>();
  return {
    setOpen: (id) => {
      if (open === id) return;
      open = id;
      listeners.forEach((listener) => listener());
    },
    current: () => open,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
const fallbackStore = createMenuStore();
const MenuContext = createContext<MenuStore>(fallbackStore);
export function MenuScope({ children }: { children: ReactNode }) {
  const [store] = useState(createMenuStore);
  return <MenuContext.Provider value={store}>{children}</MenuContext.Provider>;
}
export function useMenus(): MenuStore {
  return useContext(MenuContext);
}
/** Si el menú `id` está abierto (solo se redibuja cuando cambia para él). */
function useMenuOpen(id: string): boolean {
  const store = useMenus();
  return useSyncExternalStore(store.subscribe, () => store.current() === id);
}

const afterLayout = (run: () => void) => requestAnimationFrame(() => setTimeout(run, 0));

/**
 * Lo que hace chat-app.js (bindPortalMenu) con un menú abierto: el fondo queda inerte, el menú es un diálogo modal con el
 * nombre de su botón, Tab no sale de él, las flechas recorren sus opciones y un clic fuera o Esc lo cierran con el foco en
 * su botón.
 */
function useModalMenu(element: React.RefObject<HTMLDivElement | null>, open: boolean, dismiss: () => void) {
  const dismissRef = useRef(dismiss);
  useEffect(() => {
    dismissRef.current = dismiss;
  });
  useLayoutEffect(() => {
    const menu = element.current;
    if (!open || !menu) return;
    return bindModalMenu(menu, () => dismissRef.current());
  }, [element, open]);
}

/** El trabajo de DOM de useModalMenu; devuelve cómo deshacerlo. */
function bindModalMenu(menu: HTMLElement, dismissMenu: () => void): () => void {
  {
    const abort = new AbortController(),
      options = { signal: abort.signal };
    const previous: [HTMLElement, boolean][] = [];
    for (let branch: HTMLElement = menu; branch.parentElement; branch = branch.parentElement) {
      for (const sibling of branch.parentElement.children)
        if (sibling !== branch && sibling instanceof HTMLElement) {
          previous.push([sibling, sibling.inert]);
          sibling.inert = true;
        }
      if (branch.parentElement === document.body) break;
    }
    const attributes = ["role", "aria-modal", "aria-label"].map((name) => [name, menu.getAttribute(name)] as const);
    const trigger = () => menu.querySelector<HTMLElement>("[data-chat-menu-trigger]");
    menu.setAttribute("role", "dialog");
    menu.setAttribute("aria-modal", "true");
    menu.setAttribute("aria-label", trigger()?.getAttribute("aria-label") || (menu.textContent ?? "").trim());
    if (!menu.contains(document.activeElement)) trigger()?.focus({ preventScroll: true });
    const close = () => {
      trigger()?.focus({ preventScroll: true });
      dismissMenu();
    };
    document.addEventListener(
      "pointerdown",
      (event) => {
        if (!menu.contains(event.target as Node)) {
          event.preventDefault();
          event.stopPropagation();
          close();
        }
      },
      options,
    );
    menu.addEventListener(
      "keydown",
      (event) => {
        if (event.defaultPrevented) return;
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          close();
          return;
        }
        if (event.key === "Tab") {
          const controls = [
            ...menu.querySelectorAll<HTMLElement>(
              'button:not(:disabled),a[href],input:not(:disabled),textarea:not(:disabled),[tabindex="0"]',
            ),
          ].filter((item) => item.getClientRects().length && !item.closest("[inert]"));
          const first = controls[0],
            last = controls.at(-1);
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
          return;
        }
        if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
        if ((event.target as Element).matches("input,textarea")) return;
        const buttons = [
          ...(menu.querySelector('[role="menu"]')?.querySelectorAll<HTMLElement>("button:not(:disabled),a[href]") ??
            []),
        ].filter((item) => item.getClientRects().length);
        if (!buttons.length) return;
        const at = buttons.indexOf(document.activeElement as HTMLElement);
        const next =
          event.key === "Home"
            ? 0
            : event.key === "End"
              ? buttons.length - 1
              : (at + (event.key === "ArrowDown" ? 1 : -1) + buttons.length) % buttons.length;
        event.preventDefault();
        buttons[next]!.focus();
      },
      options,
    );
    return () => {
      abort.abort();
      for (const [item, value] of previous.reverse()) item.inert = value;
      for (const [name, value] of attributes) {
        if (value === null) menu.removeAttribute(name);
        else menu.setAttribute(name, value);
      }
      // El foco vuelve al botón después del cuadro, solo si nada lo tomó (una ventana abierta desde el menú lo conserva).
      const button = menu.isConnected ? trigger() : null;
      if (button)
        afterLayout(() => {
          const active = document.activeElement;
          if (button.isConnected && (!active || active === document.body || menu.contains(active)))
            button.focus({ preventScroll: true });
        });
    };
  }
}

type MenuProps = {
  id: string;
  className: string;
  triggerClass: string;
  ariaLabel: string;
  tooltip: string;
  summary: ReactNode;
  children: ReactNode;
};
export function TeamMenu({ id, className, triggerClass, ariaLabel, tooltip, summary, children }: MenuProps) {
  const store = useMenus();
  const element = useRef<HTMLDivElement>(null);
  const open = useMenuOpen(id);
  const close = useCallback(() => store.setOpen(null), [store]);
  useModalMenu(element, open, close);
  useRowHoverOpen(element, open);
  return (
    <div ref={element} className={className} {...(open ? { open: true } : {})} data-chat-menu={id}>
      <button
        type="button"
        data-chat-menu-trigger=""
        className={triggerClass}
        role="button"
        title={tooltip}
        aria-haspopup="menu"
        aria-label={ariaLabel}
        aria-expanded={open ? "true" : "false"}
        data-shell-tooltip={tooltip}
        onClick={(event) => {
          event.preventDefault();
          store.setOpen(open ? null : id);
        }}
      >
        {summary}
      </button>
      {open ? children : null}
    </div>
  );
}

/** ChatBottomSheet: la superficie del menú; en el celular, bajar la manija lo cierra. */
export function BottomSheet({ children }: { children: ReactNode }) {
  const store = useMenus();
  const handle = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = handle.current!;
    let start: number | null = null;
    const down = (event: PointerEvent) => {
      if (innerWidth > 767) return;
      start = event.clientY;
      element.setPointerCapture(event.pointerId);
    };
    const up = (event: PointerEvent) => {
      const distance = start === null ? 0 : event.clientY - start;
      start = null;
      if (distance >= 48 && innerWidth <= 767) store.setOpen(null);
    };
    const cancel = () => (start = null);
    element.addEventListener("pointerdown", down);
    element.addEventListener("pointerup", up);
    element.addEventListener("pointercancel", cancel);
    return () => {
      element.removeEventListener("pointerdown", down);
      element.removeEventListener("pointerup", up);
      element.removeEventListener("pointercancel", cancel);
    };
  }, [store]);
  return (
    <div className="bheli-bottom-sheet" data-bottom-sheet="">
      <div className="bheli-sheet-handle" aria-hidden="true" ref={handle}>
        <span></span>
      </div>
      {children}
    </div>
  );
}

/** TeamInterop.closeMenusOutside: el ⋮ abierto de Equipo se cierra con un clic fuera o con Esc (sin ventana modal). */
export function useCloseMenusOutside(anchor: React.RefObject<HTMLElement | null>) {
  const store = useMenus();
  const setOpen = store.setOpen;
  useEffect(() => {
    const scope = anchor.current?.closest(".tm") ?? anchor.current;
    if (!scope) return;
    const abort = new AbortController();
    document.addEventListener(
      "pointerdown",
      (event) => {
        const open = scope.querySelector("[data-chat-menu][open]");
        if (open && !open.contains(event.target as Node)) setOpen(null);
      },
      { capture: true, signal: abort.signal },
    );
    document.addEventListener(
      "keydown",
      (event) => {
        const open = scope.querySelector("[data-chat-menu][open]");
        if (event.key !== "Escape" || !open || document.querySelector("dialog:modal")) return;
        event.preventDefault();
        open.querySelector<HTMLElement>("[data-chat-menu-trigger]")?.focus({ preventScroll: true });
        setOpen(null);
      },
      { signal: abort.signal },
    );
    return () => abort.abort();
  }, [anchor, setOpen]);
}

// ---- Ventana (TeamWindow: TaskWindow + TeamWindow.razor.js) ----

export type WindowHandle = { open: () => void; close: () => void };
type WindowProps = {
  title: string;
  closeLabel: string;
  confirm?: boolean;
  children?: ReactNode;
  footer?: ReactNode;
  /** El dueño decide si cierra (Esc, × o el fondo). */
  onDismiss?: () => void;
};
let windowSeed = 0;
export const TeamWindow = forwardRef<WindowHandle, WindowProps>(function TeamWindow(props, ref) {
  const dialog = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);
  const [titleId] = useState(() => `tm-window-${(++windowSeed).toString(16).padStart(32, "0")}`);
  const dismissRef = useRef<() => void>(() => undefined);
  const close = useCallback(() => {
    const element = dialog.current;
    if (!element) return;
    if (element.open) element.close();
    const previous = opener.current;
    if (previous instanceof HTMLElement && previous.isConnected) previous.focus({ preventScroll: true });
  }, []);
  useEffect(() => {
    dismissRef.current = props.onDismiss ?? close;
  });
  useImperativeHandle(ref, () => ({
    open: () => {
      const element = dialog.current;
      if (!element) return;
      opener.current = document.activeElement;
      if (!element.open) element.showModal();
      element.querySelector<HTMLElement>("[data-tm-close]")?.focus({ preventScroll: true });
    },
    close,
  }));
  useEffect(() => {
    const element = dialog.current!;
    const abort = new AbortController();
    element.addEventListener(
      "cancel",
      (event) => {
        event.preventDefault();
        dismissRef.current();
      },
      { signal: abort.signal },
    );
    element.addEventListener(
      "pointerdown",
      (event) => {
        if (event.target !== element) return;
        const box = element.getBoundingClientRect();
        if (
          event.clientX < box.left ||
          event.clientX > box.right ||
          event.clientY < box.top ||
          event.clientY > box.bottom
        )
          dismissRef.current();
      },
      { signal: abort.signal },
    );
    return () => abort.abort();
  }, []);
  const dismiss = () => dismissRef.current();
  return (
    <TaskWindow ref={dialog} className={props.confirm ? "tm-window tm-confirm" : "tm-window"} titleId={titleId}>
      <header>
        {!props.confirm ? (
          <button
            type="button"
            className="tm-back"
            aria-label={props.closeLabel}
            title={props.closeLabel}
            onClick={dismiss}
          >
            <ChatIcon name="arrow-left" />
          </button>
        ) : null}
        <h2 id={titleId}>{props.title}</h2>
        {!props.confirm ? (
          <button
            type="button"
            className="tm-icon-btn"
            data-tm-close=""
            aria-label={props.closeLabel}
            title={props.closeLabel}
            onClick={dismiss}
          >
            <ChatIcon name="x" />
          </button>
        ) : null}
      </header>
      <div className="tm-window-body">{props.children}</div>
      {props.footer ? <footer>{props.footer}</footer> : null}
    </TaskWindow>
  );
});

// ---- Toast (TeamToast + TeamFeedback): uno solo por vista, 2,8 s, con «Deshacer» si aplica ----

type Feedback = {
  show: (message: string, undo?: () => Promise<void> | void) => void;
  /** D2 · un «Deshacer» que terminó: la pantalla a la vista vuelve a leer. */
  notifyChanged: () => void;
  /** D2 · un «Deshacer» que no pudo terminar: la pantalla a la vista lo muestra como su error. */
  notifyFailed: (message: string) => void;
  subscribe: (changed: () => void, failed: (message: string) => void) => () => void;
};
const FeedbackContext = createContext<Feedback | null>(null);
export function useFeedback(): Feedback {
  return useContext(FeedbackContext)!;
}

export function FeedbackScope({ undoLabel, children }: { undoLabel: string; children: ReactNode }) {
  const [toast, setToast] = useState<{ message: string; undo?: () => Promise<void> | void; version: number } | null>(
    null,
  );
  const timer = useRef<number | null>(null);
  const listeners = useRef(new Set<{ changed: () => void; failed: (message: string) => void }>());
  const feedback = useMemo<Feedback>(
    () => ({
      show: (message, undo) => {
        if (timer.current) clearTimeout(timer.current);
        setToast((current) => ({ message, undo, version: (current?.version ?? 0) + 1 }));
        timer.current = window.setTimeout(() => setToast(null), 2800);
      },
      notifyChanged: () => listeners.current.forEach((listener) => listener.changed()),
      notifyFailed: (message) => listeners.current.forEach((listener) => listener.failed(message)),
      subscribe: (changed, failed) => {
        const entry = { changed, failed };
        listeners.current.add(entry);
        return () => listeners.current.delete(entry);
      },
    }),
    [],
  );
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  const undo = () => {
    const action = toast?.undo;
    setToast(null);
    if (timer.current) clearTimeout(timer.current);
    if (action) void action();
  };
  return (
    <FeedbackContext.Provider value={feedback}>
      {children}
      {toast ? (
        <Toast
          key={toast.version}
          message={toast.message}
          tone="success"
          action={
            <>
              {toast.undo ? (
                <button type="button" className="tm-link" onClick={undo}>
                  {undoLabel}
                </button>
              ) : null}
            </>
          }
        />
      ) : null}
    </FeedbackContext.Provider>
  );
}

// ---- Vacío, buscador, paginado e interruptor ----

export function TeamEmpty({
  title,
  line,
  actionLabel,
  actionHref,
  onAction,
}: {
  title: string;
  line?: string | null;
  actionLabel?: string | null;
  actionHref?: string;
  onAction?: () => void;
}) {
  return (
    <div className="tm-empty" role="status" data-part="vacio">
      <strong>{title}</strong>
      {line ? <span>{line}</span> : null}
      {actionLabel ? (
        actionHref !== undefined ? (
          <a className="tm-btn secondary" href={actionHref}>
            {actionLabel}
          </a>
        ) : (
          <button type="button" className="tm-btn secondary" onClick={() => onAction?.()}>
            {actionLabel}
          </button>
        )
      ) : null}
    </div>
  );
}

/** Espera del buscador (el portal actual espera 250 ms; aquí 100 ms, como Etiquetas y el Chat de R5). */
export const searchDelay = 100;

/**
 * El campo del buscador, memorizado: React 19 reescribe `name` y `type` de un `<input>` en cada actualización aunque no
 * cambien, y esas mutaciones alargaban las acciones de Equipo (la lista se redibuja al llegar sus datos).
 */
const SearchField = memo(function SearchField({
  inputRef,
  value,
  placeholder,
  onInput,
  onEscape,
}: {
  inputRef: RefObject<HTMLInputElement | null>;
  value: string;
  placeholder: string;
  onInput: (value: string) => void;
  onEscape: () => void;
}) {
  return (
    <input
      ref={inputRef}
      className="bheli-composite-input"
      type="search"
      enterKeyHint="search"
      aria-label={placeholder}
      placeholder={placeholder}
      value={value}
      onChange={(event) => onInput(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Escape") onEscape();
      }}
    />
  );
});

export type SearchHandle = { clear: () => void };
export const TeamSearch = forwardRef<
  SearchHandle,
  { value: string; placeholder: string; clearLabel: string; onChange: (value: string) => void }
>(function TeamSearch({ value, placeholder, clearLabel, onChange }, ref) {
  const [typed, setTyped] = useState(value);
  const [applied, setApplied] = useState(value);
  const input = useRef<HTMLInputElement>(null);
  const pending = useRef<number | null>(null);
  const changeRef = useRef(onChange);
  useEffect(() => {
    changeRef.current = onChange;
  });
  // Ajustar el texto cuando el valor de afuera cambia (OnParametersSet).
  if (applied !== value) {
    setApplied(value);
    setTyped(value);
  }
  const apply = useCallback((next: string) => {
    setApplied(next);
    changeRef.current(next);
  }, []);
  const clear = useCallback(() => {
    if (pending.current) clearTimeout(pending.current);
    setTyped("");
    apply("");
    input.current?.focus();
  }, [apply]);
  // Manejadores estables para SearchField: el campo no se redibuja cuando la pantalla sí (ver ui/FileInput).
  const typedRef = useRef(typed);
  useLayoutEffect(() => {
    typedRef.current = typed;
  });
  const onInput = useCallback(
    (next: string) => {
      setTyped(next);
      if (pending.current) clearTimeout(pending.current);
      pending.current = window.setTimeout(() => apply(next), searchDelay);
    },
    [apply],
  );
  const onEscape = useCallback(() => {
    if (typedRef.current.length > 0) clear();
  }, [clear]);
  useImperativeHandle(ref, () => ({ clear }));
  useEffect(
    () => () => {
      if (pending.current) clearTimeout(pending.current);
    },
    [],
  );
  return (
    <div className="tm-search" role="search" data-part="buscador">
      <ChatIcon name="search" />
      <SearchField inputRef={input} value={typed} placeholder={placeholder} onInput={onInput} onEscape={onEscape} />
      {typed.length > 0 ? (
        <button type="button" className="tm-icon-btn" aria-label={clearLabel} title={clearLabel} onClick={clear}>
          <ChatIcon name="x" />
        </button>
      ) : null}
    </div>
  );
});

export function TeamPager({
  total,
  page,
  pageSize = 25,
  busy,
  onChange,
}: {
  total: number;
  page: number;
  pageSize?: number;
  busy: boolean;
  onChange: (page: number) => void;
}) {
  const T = useT();
  if (total <= pageSize) return null;
  return (
    <nav className="tm-pager" aria-label={T("CFG1_PAGES")} data-part="paginado">
      <span>{formatText(T("CFG1_R1_RANGE"), (page - 1) * pageSize + 1, Math.min(page * pageSize, total), total)}</span>
      <button
        type="button"
        className="tm-icon-btn"
        disabled={page <= 1 || busy}
        aria-label={T("CFG1_PREVIOUS")}
        title={T("CFG1_PREVIOUS")}
        onClick={() => onChange(page - 1)}
      >
        <ChatIcon name="chevron-left" />
      </button>
      <button
        type="button"
        className="tm-icon-btn"
        disabled={page * pageSize >= total || busy}
        aria-label={T("CFG1_NEXT")}
        title={T("CFG1_NEXT")}
        onClick={() => onChange(page + 1)}
      >
        <ChatIcon name="chevron-right" />
      </button>
    </nav>
  );
}

export function TeamSwitch({
  title,
  line,
  checked,
  disabled,
  disabledReason,
  compact,
  readOnly,
  onChange,
}: {
  title: string;
  line?: string | null;
  checked: boolean;
  disabled?: boolean;
  disabledReason?: string;
  compact?: boolean;
  readOnly?: boolean;
  onChange?: (value: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      className={`tm-switch ${compact ? "compact" : ""}`}
      aria-checked={checked ? "true" : "false"}
      aria-disabled={disabled ? "true" : undefined}
      aria-readonly={readOnly ? "true" : undefined}
      aria-label={compact ? title : undefined}
      title={disabled ? disabledReason : compact ? title : undefined}
      onClick={(event) => {
        event.stopPropagation();
        if (!disabled && !readOnly) onChange?.(!checked);
      }}
      data-part="interruptor"
    >
      <span className="tm-switch-track" aria-hidden="true"></span>
      {!compact ? (
        <span>
          <strong>{title}</strong>
          {line ? <small>{line}</small> : null}
        </span>
      ) : null}
    </button>
  );
}

// ---- Filtros (C07) ----

export type Option = { value: string; label: string };

export function TeamFilterMenu({
  id,
  label,
  allLabel,
  options,
  value,
  onChange,
  searchLabel = "",
  clearLabel = "",
}: {
  id: string;
  label: string;
  allLabel: string;
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  searchLabel?: string;
  clearLabel?: string;
}) {
  const store = useMenus();
  const [search, setSearch] = useState("");
  const selected =
    value.length === 0 ? null : (options.find((option) => option.value === value) ?? { value, label: value });
  const visible = options
    .filter((option) => search.length === 0 || contains(option.label, search))
    .sort((a, b) => (a.value === value ? 0 : 1) - (b.value === value ? 0 : 1));
  const choose = (next: string) => {
    store.setOpen(null);
    setSearch("");
    onChange(next);
  };
  return (
    <TeamMenu
      id={id}
      className="tm-menu"
      triggerClass={`tm-pill${selected === null ? "" : " on"}`}
      ariaLabel={selected === null ? label : `${label}: ${selected.label}`}
      tooltip={label}
      summary={
        <>
          {selected?.label ?? label}
          <ChatIcon name="chevron-down" />
        </>
      }
    >
      <div className="tm-menu-items tm-filter-menu" role="menu" aria-label={label}>
        <BottomSheet>
          {options.length > 6 ? (
            <div className="tm-filter-search">
              <TeamSearch value={search} onChange={setSearch} placeholder={searchLabel} clearLabel={clearLabel} />
            </div>
          ) : null}
          <button
            type="button"
            role="menuitemradio"
            aria-checked={value.length === 0 ? "true" : "false"}
            onClick={() => choose("")}
          >
            <span>{allLabel}</span>
            {value.length === 0 ? <ChatIcon name="check" /> : null}
          </button>
          {visible.map((option) => (
            <button
              key={option.value}
              type="button"
              role="menuitemradio"
              aria-checked={value === option.value ? "true" : "false"}
              onClick={() => choose(option.value)}
            >
              <span>{option.label}</span>
              {value === option.value ? <ChatIcon name="check" /> : null}
            </button>
          ))}
        </BottomSheet>
      </div>
    </TeamMenu>
  );
}

/** Un grupo de «Más filtros» con sus opciones como píldoras: una o ninguna. */
export function TeamFilterChoices({
  title,
  label = "",
  part,
  options,
  value,
  onChange,
  searchLabel,
  clearLabel,
  seeAllLabel,
}: {
  title: string;
  label?: string;
  part: string;
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  searchLabel: string;
  clearLabel: string;
  seeAllLabel: string;
}) {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);
  const term = query.trim();
  const matching = term.length === 0 ? options : options.filter((option) => contains(option.label, term));
  const visible =
    options.length <= 6 || expanded || term.length > 0
      ? matching
      : [...matching]
          .sort((a, b) => (b.value === value ? 1 : 0) - (a.value === value ? 1 : 0))
          .slice(0, matching.some((option) => option.value === value) ? 6 : 5);
  return (
    <div className="bh-filter-group" data-part={part} role="group" aria-label={title.length > 0 ? title : label}>
      {title.length > 0 ? <strong>{title}</strong> : null}
      {options.length > 6 ? (
        <TeamSearch value={query} onChange={setQuery} placeholder={searchLabel} clearLabel={clearLabel} />
      ) : null}
      <div className="tm-pills">
        {visible.map((option) => (
          <button
            key={option.value}
            type="button"
            className="tm-pill"
            aria-pressed={value === option.value ? "true" : "false"}
            onClick={() => onChange(value === option.value ? "" : option.value)}
          >
            {option.label}
          </button>
        ))}
        {!expanded && matching.length > visible.length ? (
          <button type="button" className="tm-pill" onClick={() => setExpanded(true)}>
            {formatText(seeAllLabel, matching.length)}
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ---- Avatar (ChatAvatar) ----

const isPhoneIdentity = (name: string) => /\d/.test(name) && !/\p{L}/u.test(name);
export const initials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
export const avatarTone = (id: number) => `chat-avatar-tone-${Math.trunc((((id % 360) * 137 + 73) % 360) / 30)}`;

export function Avatar({ name, identity, className }: { name: string; identity: number; className: string }) {
  return (
    <span className={`${className} ${avatarTone(identity)}`} aria-hidden="true">
      <span className="chat-avatar-content" aria-hidden="true">
        {isPhoneIdentity(name) ? <ChatIcon name="user" /> : initials(name)}
      </span>
    </span>
  );
}
